<script setup lang="ts">
import { useRouter } from 'vue-router';
import { computed, ref, type PropType } from 'vue';
import RoundedButton from '@/components/ui/RoundedButton.vue';
import * as Sentry from '@sentry/vue';
import { storeToRefs } from 'pinia';
import { useKycSessionStore } from '@/stores/kycSession.store';
import type { Screen } from '@/models/UserFlow';
import Loading from '@/components/ui/Loading.vue';

type FlowNavigationDirection = 'back' | 'forward';

const props = defineProps({
  text: String,
  direction: {
    type: String as PropType<FlowNavigationDirection>,
    required: true,
  },
  preNavigationAction: Function as PropType<() => Promise<boolean> | boolean>,
  disabled: Boolean,
});

const isLoading = ref(false);

const router = useRouter();
const { kycSession } = storeToRefs(useKycSessionStore());

if (!kycSession.value) {
  throw new Error('kycSession is not defined');
}

const steps = kycSession.value.steps;

const nextRoute = computed(() => {
  const currentRoute = (router.currentRoute?.value.name || 'Region') as Screen;
  return props.direction === 'forward'
    ? steps.getNextScreen(currentRoute)
    : steps.getPreviousScreen(currentRoute);
});

const dataTestValue = computed(() => `move-to-${nextRoute.value.toLowerCase()}`);

async function performNavigation() {
  isLoading.value = true;
  if (props.preNavigationAction) {
    try {
      const isActionCompletedSuccessfully = await props.preNavigationAction();
      if (!isActionCompletedSuccessfully) {
        isLoading.value = false;
        return;
      }
    } catch (error) {
      Sentry.captureException(error, {
        level: 'info',
      });
      isLoading.value = false;
      return;
    }
  }

  await router.push({ name: nextRoute.value });
  isLoading.value = false;
}
</script>

<template>
  <RoundedButton
    v-if="nextRoute !== router.currentRoute?.value.name"
    @clicked="performNavigation"
    :class="
      props.direction === 'forward'
        ? 'dark:disabled:border-dark-brd-dark bg-light-primary text-light-white disabled:bg-light-secondary disabled:text-light-disabled dark:bg-dark-item dark:text-dark-secondary dark:disabled:bg-dark-item dark:disabled:text-dark-disabled m-2 mr-0 w-60 only:w-full sm:only:absolute sm:only:right-0 sm:only:w-60'
        : 'text-light-primary dark:text-dark-primary min-w-fit border-none bg-none py-3'
    "
    :data-test="dataTestValue"
    :disabled="props.disabled || isLoading"
    ><span v-if="direction === 'back'" class="text-xl">&#8592;&nbsp;</span
    ><Loading class="mr-2 h-5 w-5" v-if="isLoading && direction === 'forward'" />{{
      props.text
    }}</RoundedButton
  >
</template>
